import React from 'react';
import {
  Box,
  Button,
  Alert,
  Typography,
  CircularProgress,
  Skeleton
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';
import { isSafetyStaffNotAssignedError } from 'shared/services/registrationService';

interface MobileNavigationButtonsProps {
  currentStep: number;
  totalSteps: number;
  loading: boolean;
  generalError: string;
  onBack: () => void;
  onNext: () => void;
  /** Extra `Siguiente`-button disable signal (e.g. terms not yet
   *  ticked on the final step). When omitted defaults to false. */
  nextDisabled?: boolean;
}

export const MobileNavigationButtons: React.FC<MobileNavigationButtonsProps> = ({
  currentStep,
  totalSteps,
  loading,
  generalError,
  onBack,
  onNext,
  nextDisabled = false,
}) => {
  return (
    <Box sx={{
      display: { xs: 'block', md: 'none' },
      position: 'fixed',
      bottom: 30,
      left: 0,
      right: 0,
      backgroundColor: 'white',
      zIndex: 999
    }}>
      {generalError && (
        <Box sx={{ px: 3, pb: 2 }}>
          {isSafetyStaffNotAssignedError(undefined, generalError) ? (
            <Alert severity="warning">
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
                Tu empresa todavía no está lista para nuevos usuarios
              </Typography>
              <Typography variant="body2">{generalError}</Typography>
            </Alert>
          ) : (
            <Alert severity="error">
              <Box component="pre" sx={{
                fontFamily: 'inherit',
                margin: 0,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word'
              }}>
                {generalError}
              </Box>
            </Alert>
          )}
        </Box>
      )}

      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        px: 3,
        pb: 2
      }}>
      {loading ? (
        <>
          {(currentStep || 0) > 0 && (
            <Skeleton
              variant="rounded"
              width="48%"
              height={48}
              sx={{
                mr: 1,
                borderRadius: 1,
                transform: 'none'
              }}
            />
          )}
          <Skeleton
            variant="rounded"
            width={(currentStep || 0) > 0 ? "48%" : "100%"}
            height={48}
            sx={{
              borderRadius: 1,
              transform: 'none',
              ml: (currentStep || 0) > 0 ? 1 : 0
            }}
          />
        </>
      ) : (
        <>
          {currentStep > 0 && (
            <Button
              variant="outlined"
              size="large"
              onClick={onBack}
              disabled={loading}
              startIcon={<ArrowBackIcon />}
              fullWidth
              sx={{
                mr: 2,
                minHeight: '48px',
                fontSize: '1rem'
              }}
            >
              Anterior
            </Button>
          )}
          <Button
            variant="contained"
            size="large"
            onClick={onNext}
            disabled={loading || nextDisabled}
            endIcon={loading ? <CircularProgress size={20} /> : <ArrowForwardIcon />}
            fullWidth
            sx={{
              minHeight: '48px',
              fontSize: '1rem'
            }}
          >
            {currentStep === totalSteps - 1 ? 'Completar Registro' : 'Siguiente'}
          </Button>
        </>
      )}
      </Box>
    </Box>
  );
};
